/* ═══════════════════════════════════════════════════════════════════
   BLISS · POWER — Web Audio synth utility
   Pure client-side procedural sounds. Zero cloud cost.
   ─────────────────────────────────────────────────────────────────── */
import { getAudioCtx } from './audioCore';


let _ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  _ctx = getAudioCtx();
  return _ctx;
};

/** Small helper — plays a swept-frequency tone with an envelope. */
const tone = ({ f0, f1, dur = 0.25, type = 'sine', gain = 0.35, delay = 0 } = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t);
  if (f1 != null) osc.frequency.linearRampToValueAtTime(f1, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t); osc.stop(t + dur + 0.05);
};

/** Short white-noise burst (used inside animal sounds). */
const noise = ({ dur = 0.25, gain = 0.15, filterFreq = 2000, delay = 0 } = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + delay;
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.6;
  const src = ctx.createBufferSource();
  const filt = ctx.createBiquadFilter();
  const g = ctx.createGain();
  src.buffer = buf;
  filt.type = 'bandpass'; filt.frequency.value = filterFreq; filt.Q.value = 2;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filt).connect(g).connect(ctx.destination);
  src.start(t);
};

/* ─── Public sound cues ─── */

/** A clean short "tik" — plays each second of the timer. */
export const tick = () => tone({ f0: 1400, dur: 0.06, type: 'square', gain: 0.22 });

/** LION sound — thunderous roar, used on Start & Restart. */
export const lionRoar = () => {
  noise({ dur: 0.7, gain: 0.32, filterFreq: 300 });
  tone({ f0: 180, f1: 75, dur: 0.7, type: 'sawtooth', gain: 0.4 });
  tone({ f0: 300, f1: 130, dur: 0.7, type: 'triangle', gain: 0.24, delay: 0.06 });
  tone({ f0: 90,  f1: 45,  dur: 0.55, type: 'sine',     gain: 0.28, delay: 0.15 });
};

/** FALCON + PHOENIX sound — piercing screech followed by rising phoenix flourish.
    Used for Stop AND for timer End. */
export const falconPhoenix = () => {
  // Falcon screech (falling)
  tone({ f0: 1400, f1: 500,  dur: 0.32, type: 'sawtooth', gain: 0.32 });
  tone({ f0: 900,  f1: 350,  dur: 0.25, type: 'triangle', gain: 0.2, delay: 0.15 });
  // Phoenix rebirth (rising sweep)
  tone({ f0: 300,  f1: 1200, dur: 0.4,  type: 'sine',     gain: 0.28, delay: 0.35 });
  tone({ f0: 500,  f1: 1800, dur: 0.35, type: 'triangle', gain: 0.22, delay: 0.5 });
  noise({ dur: 0.5, gain: 0.12, filterFreq: 3500, delay: 0.35 });
};

/* Back-compat aliases (kept so existing imports keep working) */
export const lionBoom = lionRoar;
export const falconScreech = falconPhoenix;
export const leopardRoar = lionRoar;

/** Spoken cue via speechSynthesis (used for "3-2-1-Begin"). */
/* Deferred off the tap/tick frame: Android's TTS bridge can block the UI
   thread, so the number + tick sound render first, the voice follows. */
export const speak = (text, { rate = 0.9, pitch = 0.85, volume = 0.9 } = {}) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  setTimeout(() => {
    try {
      const ss = window.speechSynthesis;
      if (ss.speaking || ss.pending) ss.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate; u.pitch = pitch; u.volume = volume;
      ss.speak(u);
    } catch { /* silent */ }
  }, 0);
};

export const stopSpeech = () => {
  try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
};
